import React from 'react'
import {
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import {
  Comment as CommentIcon,
  PersonAdd as FollowIcon,
  Favorite as LikeIcon,
  RateReview as ReviewIcon,
  Security as SecurityIcon,
  Campaign as AnnouncementIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
} from '@mui/icons-material'
import { Notification } from '@store/api/notificationApi'

interface Props {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
}

const typeIconMap: Record<string, React.ReactElement> = {
  comment: <CommentIcon fontSize="small" />,
  follow: <FollowIcon fontSize="small" />,
  like: <LikeIcon fontSize="small" color="error" />,
  new_review: <ReviewIcon fontSize="small" />,
  system_alert: <SecurityIcon fontSize="small" color="warning" />,
  announcement: <AnnouncementIcon fontSize="small" color="info" />,
}

const typeColorMap: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  comment: 'primary',
  follow: 'success',
  like: 'error',
  new_review: 'secondary',
  system_alert: 'warning',
  announcement: 'info',
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const NotificationItem: React.FC<Props> = ({ notification, onMarkAsRead, onDelete }) => {
  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id)
    }
  }

  return (
    <ListItem
      alignItems="flex-start"
      onClick={handleClick}
      sx={{
        cursor: notification.isRead ? 'default' : 'pointer',
        backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
        '&:hover': { backgroundColor: 'action.selected' },
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        pr: 1,
      }}
      secondaryAction={
        <IconButton
          edge="end"
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(notification.id)
          }}
          aria-label="delete notification"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
        {typeIconMap[notification.type] ?? <CircleIcon fontSize="small" />}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={notification.isRead ? 400 : 600} component="span">
              {notification.title}
            </Typography>
            {!notification.isRead && (
              <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
            )}
            <Chip
              label={notification.type.replace('_', ' ')}
              size="small"
              color={typeColorMap[notification.type] ?? 'default'}
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          </Box>
        }
        secondary={
          <>
            <Typography variant="body2" color="text.secondary" component="span">
              {notification.message}
            </Typography>
            <Typography variant="caption" color="text.disabled" component="span" sx={{ display: 'block' }}>
              {formatDate(notification.createdAt)}
            </Typography>
          </>
        }
      />
    </ListItem>
  )
}

export default NotificationItem
